import { memo, useState, useEffect } from 'react'
import { EditorProps } from 'react-data-grid'
import { DgtPdIAMUser, Division, ProductionLine, Department } from '../models'
import Modal from '../components/Modal'
import { DepartmentRoles } from '../constants/Enum'

import { useForm, SubmitHandler } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from "yup";

import Select from 'react-select';
import { Badge, Alert } from '@aws-amplify/ui-react';

import { 
  updateUser,
  resetCognitoPassword,
  getCognitoUserStatus
} from '../apis'

import { SubmitState } from '../constants/EnumType'

export default memo(function UserEditor({ props } : {
  props: EditorProps<DgtPdIAMUser>
}) {
  const { onClose, onRowChange, row } = props

  const schema = yup.object({
    employeeNo: yup.string().required('กรอกรหัสพนักงาน 6 หลัก').length(6, 'กรอกรหัสพนักงาน 6 หลัก'),
    firstName: yup.string().required('กรอกชื่อจริง'),
    lastName: yup.string().required('กรอกนามสุกล'),
    roles: yup.string().required('เลือกอย่างน้อย 1 Role')
  })

  const { 
    register, handleSubmit,
    formState: { errors },
    setValue
  } = useForm<DgtPdIAMUser>({
    defaultValues: {
      username: row.username,
      email: row.email,
      employeeNo: row.employeeNo,
      firstName: row.firstName,
      lastName: row.lastName,
      division: row.division,
      department: row.department,
      line: row.line,
      isAdmin: row.isAdmin,
      active: row.active,
      roles: row.roles
    },
    resolver: yupResolver(schema)
  })

  const currentRoles = row.roles ? 
    (JSON.parse(row.roles) as { name: string, readOnly: boolean }[]).map(role => ({ value: `${role.name}${role.readOnly ? '-ro':''}`, label: `${role.name}${role.readOnly ? ' - Read Only' : ''}` })) : 
    []
  const [ selectedRoles, setSelectedRoles ] = useState<{ value: string, label: string}[]>(currentRoles)

  const [ loading, setLoading ] = useState<boolean>(false)
  const [ resetCompleted, setResetComplete] = useState<SubmitState>(SubmitState.INIT)
  const [ errorFromApi, setErrorFromApi ] = useState<string>()
  const [ cognitoStatus, setCognitoStatus ] = useState<string>()

  const roles = DepartmentRoles[row.department as Department]

  const rolesWithReadOnly = [
    ...roles.map(role => ({ label: role, value: role, isDisabled: selectedRoles.map(r => r.value).includes(`${role}-ro`) })),
    ...roles.map(role => ({ label: `${role} - Read Only`, value: `${role}-ro`, isDisabled: selectedRoles.map(r => r.value).includes(role) }))
  ]

  useEffect(() => {
    const arrayRoles = selectedRoles.map(role => (
      { name: role.value.replace('-ro', ''), readOnly: role.value.includes('-ro') }
    ))
    setValue('roles', arrayRoles.length > 0 ? JSON.stringify(arrayRoles) : undefined)
  }, [ selectedRoles, setValue ])

  useEffect(() => {
    const getUserStatus = async (username: string) => {
      const userInfo = await getCognitoUserStatus(username)
      if (userInfo.statusCode === 200) setCognitoStatus(userInfo.userStatus)
    }
    getUserStatus(row.username)
  }, [ row ])

  const onSubmitForm: SubmitHandler<DgtPdIAMUser> = async (data) => {
    console.log(data)
    setLoading(true)
    try {
      await updateUser(data.username, data)
      setLoading(false)
      onRowChange(data, true)
    } catch (error) {
      console.log(error)
      setLoading(false)
      setErrorFromApi('Error จากระบบ แจ้งผู้พัฒนา') 
    }
  }

  const onResetPassword = async () => {
    setLoading(true)
    
    setResetComplete(SubmitState.INIT)
    try {
      await resetCognitoPassword(row.username)
      setResetComplete(SubmitState.COMPLETED)
    } catch (error) {
      console.error(error)
      setResetComplete(SubmitState.FAILED)
    }
    setLoading(false)
  }

  return <Modal 
    show={true}
    onHide={onClose}
    maxWidth='max-w-xl'
    title={`แก้ไข User - ${row.username}`}>
    <>
      {row.username.startsWith('digital-production-') ? null : 
      <div className='flex items-center justify-between p-3 bg-red-100 border border-red-500'>
        <label className="inline-block mb-2 font-bold text-red-700 underline form-label">Password Zone</label>
        <button className=' btn-outline-danger' onClick={onResetPassword} disabled={loading}>Reset รหัสผ่าน ?</button>
      </div>
      }
      {resetCompleted === SubmitState.COMPLETED ? <Alert
        style={{ marginTop: 16 }}
        variation="success"
        isDismissible={true}
        hasIcon={true}
        heading="Reset รหัสผ่านเรียบร้อย"
        >
        เข้าระบบใหม่ด้วยรหัสผ่านชั่วคราว neofactory
      </Alert> : null}
      {resetCompleted === SubmitState.FAILED ? <Alert
        style={{ marginTop: 16 }}
        variation='error'
        isDismissible={true}
        hasIcon={true}
        heading="Reset รหัสผ่านไม่สำเร็จ"
        >
        กรุณาแจ้งผู้ดูแลระบบ
      </Alert> : null}
      <form onSubmit={handleSubmit(onSubmitForm)} className='p-3 ' >
        <label className="inline-block mb-2 font-bold underline form-label">แก้ไขรายละเอียด User</label>
        <div className='grid grid-cols-2 gap-3'>
          <div>
            <label htmlFor='employeeNo' className="inline-block after:content-['*'] after:ml-0.5 after:text-red-500 mb-2 text-gray-700 form-label">รหัสพนักงาน</label>
            <input
              type='text'
              className=" input-form-text"
              id='employeeNo'
              placeholder='กรอกรหัสพนักงาน 6 หลัก'
              disabled={loading}
              {...register('employeeNo')}
            />
            {errors && errors.employeeNo ? <small className="error-label">{errors.employeeNo.message}</small> : null}
            </div>
          <div>
            <label htmlFor='email' className="inline-block mb-2 text-gray-700 form-label">อีเมล์</label>
            <input
              type='email'
              className=" input-form-text"
              id='email'
              placeholder='กรอกอีเมล์ (ถ้ามี)'
              disabled={loading}
              {...register('email')}
            />
            {errors && errors.email ? <small className="error-label">{errors.email.message}</small> : null}
          </div>
          <div>
            <label htmlFor='firstName' className="inline-block mb-2 text-gray-700 after:content-['*'] after:ml-0.5 after:text-red-500 form-label">ชื่อ</label>
            <input
              type='text'
              className=" input-form-text"
              id='firstName'
              placeholder='ชื่อ'
              disabled={loading}
              {...register('firstName')}
            />
            {errors && errors.firstName ? <small className="error-label">{errors.firstName.message}</small> : null}
          </div>
          <div>
            <label htmlFor='lastName' className="inline-block mb-2 text-gray-700 after:content-['*'] after:ml-0.5 after:text-red-500 form-label">สกุล</label>
            <input
              type='text'
              className=" input-form-text"
              id='lastName'
              placeholder='สกุล'
              disabled={loading}
              {...register('lastName')}
            />
            {errors && errors.lastName ? <small className="error-label">{errors.lastName.message}</small> : null}
          </div>
          <div className='col-span-2 '>
            <label htmlFor='role' className="inline-block mb-2 text-gray-700 after:content-['*'] after:ml-0.5 after:text-red-500 form-label">Role</label>
            <Select
              isDisabled={loading}
              isMulti
              name="roles"
              placeholder="เลือกอย่างน้อย 1 Role"
              menuPortalTarget={document.body}
              styles={{ 
                menuPortal: base => ({ ...base, zIndex: 9999 }),
                multiValueLabel: (styles, { data }) => ({
                  ...styles,
                  color: data.value.includes('-ro') ? 'gray' : 'green',
                })
              }}
              options={rolesWithReadOnly}
              className="basic-multi-select"
              classNamePrefix="select"
              value={selectedRoles}
              onChange={vals => setSelectedRoles(Array.from(vals))}
            />
            {errors && errors.roles && selectedRoles.length === 0 ? <small className="error-label">{errors.roles.message}</small> : null}
          </div>
          <div>
            <label htmlFor='division' className="inline-block mb-2 text-gray-700 form-label">Division</label>
            <div className="flex gap-2">
              {Object.values(Division).map(division => 
                <div key={division} className="flex form-check form-check-inline">
                  <input {...register('division')} title={division} className="form-check-input input-radio" type="radio"
                    name="division" id={`division_${division}`} value={division} disabled={loading} />
                  <label className="inline-block text-gray-800 form-check-label" htmlFor={`division_${division}`}>{division}</label>
                </div>
              )}
            </div>
          </div>
          <div>
            <label htmlFor='line' className="inline-block mb-2 text-gray-700 form-label">Line ผลิต</label>
            <div className="flex gap-2">
              {Object.values(ProductionLine).map(pdLine => 
                <div key={pdLine} className="flex form-check form-check-inline">
                  <input {...register('line')} title={pdLine} className="form-check-input input-radio" type="radio"
                    name="line" id={`pdLine_${pdLine}`} value={pdLine} disabled={loading}/>
                  <label className="inline-block text-gray-800 form-check-label" htmlFor={`pdLine_${pdLine}`}>{pdLine}</label>
                </div>
              )}
            </div>
          </div>
          <div className='col-span-2 '>
            <label htmlFor='isAdmin' className="inline-block mb-2 text-gray-700 form-label">อื่นๆ</label>
            <div className="form-check">
              <input className="float-left w-4 h-4 mt-1 mr-2 align-top" type="checkbox" value="" id="isManageUsers"
                { ...register('isAdmin')} disabled={loading} />
              <label className="inline-block text-gray-800 form-check-label" htmlFor="isManageUsers">
                สามารถจัดการ User ได้ (เพิ่ม/ลด/แก้ไข Role)
              </label>
            </div>
          </div>
          <div className='col-span-2 '>
            <label htmlFor='isActive' className="inline-block mb-2 text-gray-700 form-label">สถานะ</label>
            <div className="form-check">
              <input className="float-left w-4 h-4 mt-1 mr-2 align-top" type="checkbox" value="" id="isActive"
                { ...register('active')} disabled={loading} />
              <label className="inline-block text-gray-800 form-check-label" htmlFor="isActive">
                Active
              </label>
            </div>
          </div>
          <div className='col-span-2 '>
            <label htmlFor='cognitoStatus' className="inline-block mb-2 text-gray-700 form-label">สถานะ Cognito <span className='text-xs italic text-muted '>(สำหรับ IT Admin)</span></label>
            <div className="form-check">
              {cognitoStatus ? <Badge variation={cognitoStatus === 'CONFIRMED'? 'success' : 'warning'} size='small'>{cognitoStatus}</Badge> : '-'}
            </div>       
          </div>
        </div>
        {errorFromApi ? <small className='text-red-500 '>{errorFromApi}</small> : null}
        <button type='submit' disabled={loading} className="float-right ml-3 btn-success" >บันทึก</button>
        <button type='button' disabled={loading} className="float-right text-base btn-outline-success" onClick={() => onClose(false)}>
          ปิด
        </button>
      </form>
    </>    
  </Modal>
})